import React, { useState, useEffect, useMemo } from 'react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  TabContainer,
  TabButton,
  ModalTitle,
  ImportContainer,
  FormGroup,
  Label,
  Select,
  FileInput,
  RadioGroup,
  RadioLabel,
  SavesList,
  GroupHeader,
  SaveItem,
  ModalActions,
  GroupContainer,
  Button,
  Checkbox,
  CloseButton,
  FileInputContainer,
  FileInputLabel,
  MessageBox,
} from '../../styles/ImportExportSavesModal.styles';
import ChevronIcon from '../icons/ChevronIcon';
import { parseImportedFile } from '../../utils/importUtils';

const ImportExportSavesModal = ({ isOpen, onClose, allSaves, filteredSaves, tabs, onExport, onCopy, addSave }) => {
  const [activeTab, setActiveTab] = useState('export');
  const [selectedSaves, setSelectedSaves] = useState(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [format, setFormat] = useState('json');
  const [exportScope, setExportScope] = useState('current'); // 'current' or 'all'
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('direct');
  const [targetTab, setTargetTab] = useState(null);
  const [parsedSaves, setParsedSaves] = useState([]);
  const [selectedSavesForImport, setSelectedSavesForImport] = useState(new Set());
  const [importSummary, setImportSummary] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const savesToSelect = exportScope === 'current' ? filteredSaves : allSaves;
      setSelectedSaves(new Set(savesToSelect.map(s => s.id)));
      const allGroupIds = ['read', 'unread', 'archived', ...tabs.map(t => t.id)];
      setCollapsedGroups(new Set(allGroupIds));
    }
  }, [isOpen, allSaves, filteredSaves, tabs, exportScope]);

  useEffect(() => {
    if (!importFile) {
      setParsedSaves([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      const fileExtension = importFile.name.split('.').pop().toLowerCase();
      try {
        const parsedData = await parseImportedFile(content, fileExtension);
        const nonDuplicateSaves = parsedData.filter(
          (save) => !allSaves.some((existingSave) => existingSave.url === save.url)
        );
        const duplicateCount = parsedData.length - nonDuplicateSaves.length;
        setParsedSaves(nonDuplicateSaves);
        setSelectedSavesForImport(new Set(nonDuplicateSaves.map(s => s.url)));
        setImportSummary({
          found: parsedData.length,
          new: nonDuplicateSaves.length,
          duplicates: duplicateCount,
        });
      } catch (error) {
        console.error(error);
        // Here you could show a toast to the user
      }
    };
    reader.readAsText(importFile);
  }, [importFile, allSaves]);

  useEffect(() => {
    const originalOverflow = window.getComputedStyle(document.body).overflow;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const handleSelectSaveForImport = (saveUrl) => {
    setSelectedSavesForImport(prev => {
      const newSet = new Set(prev);
      if (newSet.has(saveUrl)) newSet.delete(saveUrl);
      else newSet.add(saveUrl);
      return newSet;
    });
  };

  const handleSelectAllForImport = () => {
    if (selectedSavesForImport.size === parsedSaves.length) {
      setSelectedSavesForImport(new Set());
    } else {
      setSelectedSavesForImport(new Set(parsedSaves.map(s => s.url)));
    }
  };

  const handleImport = () => {
    const savesToImport = parsedSaves.filter(s => selectedSavesForImport.has(s.url));
    savesToImport.forEach(save => {
      const newSave = {
        title: save.title,
        url: save.url,
        tags: save.tags || [],
        read: save.read || false,
        archived: save.archived || false,
        tabId: importMode === 'tab' ? targetTab : (save.tabId || null),
      };
      addSave(newSave);
    });
    onClose();
  };

  const groupedSaves = useMemo(() => {
    const groups = {
      read: { name: 'Read', saves: [] },
      unread: { name: 'Unread', saves: [] },
      archived: { name: 'Archived', saves: [] },
    };
    tabs.forEach(tab => {
      groups[tab.id] = { name: tab.name, saves: [] };
    });

    const savesToGroup = exportScope === 'current' ? filteredSaves : allSaves;
    savesToGroup.forEach(save => {
      if (save.archived) {
        groups.archived.saves.push(save);
      } else if (save.tabId && groups[save.tabId]) {
        groups[save.tabId].saves.push(save);
      } else if (save.read) {
        groups.read.saves.push(save);
      } else {
        groups.unread.saves.push(save);
      }
    });

    return Object.entries(groups).filter(([_, group]) => group.saves.length > 0);
  }, [allSaves, filteredSaves, tabs, exportScope]);

  const handleToggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) newSet.delete(groupId);
      else newSet.add(groupId);
      return newSet;
    });
  };

  const handleSelectGroup = (savesInGroup) => {
    const allIdsInGroup = savesInGroup.map(s => s.id);
    const areAllSelected = allIdsInGroup.every(id => selectedSaves.has(id));

    setSelectedSaves(prev => {
      const newSet = new Set(prev);
      if (areAllSelected) {
        allIdsInGroup.forEach(id => newSet.delete(id));
      } else {
        allIdsInGroup.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleSelectSave = (saveId) => {
    setSelectedSaves(prev => {
      const newSet = new Set(prev);
      if (newSet.has(saveId)) newSet.delete(saveId);
      else newSet.add(saveId);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const savesToSelect = exportScope === 'current' ? filteredSaves : allSaves;
    if (selectedSaves.size === savesToSelect.length) {
      setSelectedSaves(new Set());
    } else {
      setSelectedSaves(new Set(savesToSelect.map(s => s.id)));
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    onClose();
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent onClick={handleContentClick}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ModalHeader>
          <ModalTitle>Import / Export Saves</ModalTitle>
          <TabContainer>
            <TabButton active={activeTab === 'export'} onClick={() => setActiveTab('export')}>
              Export
            </TabButton>
            <TabButton active={activeTab === 'import'} onClick={() => setActiveTab('import')}>
              Import
            </TabButton>
          </TabContainer>
        </ModalHeader>
        {activeTab === 'export' && (
          <>
            <FormGroup style={{ marginTop: '16px', marginBottom: '16px', paddingLeft: '16px', paddingRight: '16px' }}>
              <Label>Export Scope</Label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="export-scope"
                    value="current"
                    checked={exportScope === 'current'}
                    onChange={() => setExportScope('current')}
                  />
                  Current View ({filteredSaves.length} saves)
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="export-scope"
                    value="all"
                    checked={exportScope === 'all'}
                    onChange={() => setExportScope('all')}
                  />
                  All Saves ({allSaves.length} saves)
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            <SavesList>
              {groupedSaves.map(([groupId, group]) => {
                const isCollapsed = collapsedGroups.has(groupId);
                const allInGroupSelected = group.saves.every(s => selectedSaves.has(s.id));

                return (
                  <GroupContainer key={groupId}>
                    <GroupHeader onClick={() => handleToggleGroup(groupId)}>
                      <ChevronIcon className={`icon ${isCollapsed ? 'collapsed' : ''}`} />
                      <Checkbox
                        checked={allInGroupSelected}
                        onChange={() => handleSelectGroup(group.saves)}
                        label={`${group.name} (${group.saves.length})`}
                      />
                    </GroupHeader>
                    {!isCollapsed && group.saves.map(save => (
                      <SaveItem key={save.id}>
                        <Checkbox
                          checked={selectedSaves.has(save.id)}
                          onChange={() => handleSelectSave(save.id)}
                          label={save.title}
                        />
                      </SaveItem>
                    ))}
                  </GroupContainer>
                );
              })}
            </SavesList>
            <ModalActions>
              <Checkbox
                checked={selectedSaves.size === (exportScope === 'current' ? filteredSaves : allSaves).length && (exportScope === 'current' ? filteredSaves : allSaves).length > 0}
                onChange={handleSelectAll}
                label="Select All"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Select value={format} onChange={(e) => setFormat(e.target.value)}>
                  <option value="json">JSON</option>
                  <option value="yaml">YAML</option>
                  <option value="txt">TXT</option>
                  <option value="csv">CSV</option>
                </Select>
                <Button className="secondary" onClick={onClose}>Cancel</Button>
                <Button className="secondary" onClick={() => onCopy(Array.from(selectedSaves), format)}>
                  Copy ({selectedSaves.size})
                </Button>
                <Button className="primary" onClick={() => onExport(Array.from(selectedSaves), format)}>
                  Export ({selectedSaves.size})
                </Button>
              </div>
            </ModalActions>
          </>
        )}
        {activeTab === 'import' && (
          <>
            <ImportContainer>
              <FormGroup>
                <Label>1. Select File</Label>
                <FileInputContainer>
                  <FileInputLabel htmlFor="import-file">
                    {importFile ? importFile.name : 'Click or drag file to import'}
                  </FileInputLabel>
                  <FileInput
                    type="file"
                    id="import-file"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    accept=".json,.yaml,.txt,.csv"
                  />
                </FileInputContainer>
              </FormGroup>

              {importSummary && (
                <MessageBox type="info">
                  Found {importSummary.found} saves. {importSummary.new} are new and {importSummary.duplicates} are duplicates (will be skipped).
                </MessageBox>
              )}

              {parsedSaves.length > 0 && (
                <>
                  <FormGroup>
                    <Label>2. Choose Import Method</Label>
                    <RadioGroup>
                      <RadioLabel>
                        <input
                          type="radio"
                          name="import-mode"
                          value="direct"
                          checked={importMode === 'direct'}
                          onChange={() => setImportMode('direct')}
                        />
                        Preserve original structure (Tabs, Archived, etc.)
                      </RadioLabel>
                      <RadioLabel>
                        <input
                          type="radio"
                          name="import-mode"
                          value="tab"
                          checked={importMode === 'tab'}
                          onChange={() => setImportMode('tab')}
                        />
                        Move all new saves into a specific tab
                      </RadioLabel>
                    </RadioGroup>
                  </FormGroup>

                  {importMode === 'tab' && (
                    <FormGroup>
                      <Label htmlFor="target-tab">Select Target Tab</Label>
                      <Select
                        id="target-tab"
                        value={targetTab || ''}
                        onChange={(e) => setTargetTab(e.target.value)}
                      >
                        <option value="" disabled>
                          Please select a tab...
                        </option>
                        {tabs.map((tab) => (
                          <option key={tab.id} value={tab.id}>
                            {tab.name}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}
                </>
              )}
            </ImportContainer>

            {parsedSaves.length > 0 && (
              <ModalActions>
                <span>
                  {selectedSavesForImport.size} / {parsedSaves.length} saves selected
                </span>
                <Button
                  className="primary"
                  onClick={handleImport}
                  disabled={(importMode === 'tab' && !targetTab) || selectedSavesForImport.size === 0}
                >
                  Import Selected
                </Button>
              </ModalActions>
            )}
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ImportExportSavesModal;
